import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { StatusComponent } from '../../../core/components/status/status.component';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { processDateRange } from '../../../../helper/date-utils';
import { Client, CommandDataOut, CommandMessageType, CommandQueryDataIn, CommandStatus, PageInfo, SortingType } from '../../../../api/api-reference';
import { SignalRService } from '../../../../services/signalr.service';
import { SignalRBaseComponent } from '../../../../services/signalr-base.component';
import { DiagnosticsModalComponent } from '../device-details/diagnostics-modal/diagnostics-modal.component';
import { DiagnosticsStateService, DiagnosticsStepResult, toDiagnosticsSteps } from '../../../../services/diagnostics-state.service';
import { ConfirmDialogComponent } from '../../../core/components/confirm-dialog/confirm-dialog.component';
import { RelayStateService } from '../../../../services/relay-state.service';
import { OnDemandReadStateService } from '../../../../services/on-demand-read-state.service';

@Component({
  selector: 'app-commands',
  standalone: true,
  imports: [CommonModule, TranslateModule, AngularSvgIconModule, IsNewDirective, PaginationComponent, SortComponent, StatusComponent, DatepickerComponent, DiagnosticsModalComponent, ConfirmDialogComponent],
  templateUrl: './commands.component.html',
  styleUrl: './commands.component.scss',
})
export class CommandsComponent extends SignalRBaseComponent {
  @Input() meterId = '';

  commands: CommandDataOut[] = [];
  numberOfCommands = 0;
  lastPageSize = 1;

  pageSize = 10;
  currentPage = 1;

  commandStatuses: CommandStatus[] = Object.values(CommandStatus);

  // The command types a device can carry, alphabetically. Device and meter model lifecycle types,
  // the desktop app types and the externally initiated token variants are left out - a row of those
  // types is still listed, it just has no filter entry of its own.
  commandTypes: CommandMessageType[] = [
    CommandMessageType.AlarmsRead,
    CommandMessageType.BillingDataOnDemand,
    CommandMessageType.BulkRemoteTokenExecution,
    CommandMessageType.Diagnostics,
    CommandMessageType.DiagnosticsPing,
    CommandMessageType.EnergyRegisterOnDemand,
    CommandMessageType.ImageUpdate,
    CommandMessageType.InstantaneousOnDemand,
    CommandMessageType.LimitReconfiguration,
    CommandMessageType.LoadClearCreditToken,
    CommandMessageType.LoadClearTamperToken,
    CommandMessageType.LoadKeyChangeToken,
    CommandMessageType.LoadPrepaidCreditToken,
    CommandMessageType.LoadProfileOnDemand,
    CommandMessageType.LoadProfileReadBuffer,
    CommandMessageType.LowLevelPasswordChange,
    CommandMessageType.MeterModelReconfiguration,
    CommandMessageType.OnDemandAlarms,
    CommandMessageType.OnDemandClockSync,
    CommandMessageType.OnDemandEventLogRead,
    CommandMessageType.OnDemandGenericAttributeRead,
    CommandMessageType.OnDemandInitSetup,
    CommandMessageType.PingDevice,
    CommandMessageType.PowerDemandRegisterOnDemand,
    CommandMessageType.ReadCreditBalanceAmount,
    CommandMessageType.ReconfigurationScheduled,
    CommandMessageType.SetConnectedState,
  ];

  requestData = new CommandQueryDataIn({
    pageInfo: new PageInfo({ pageSize: 10, page: 1 }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
    type: undefined,
  });

  isDatepickerOpen = false;
  startDate?: Date;
  endDate?: Date;
  selectedDate: string | null = '';

  activeRow: number | null = null;

  isCancelModalOpen = false;
  private commandToCancel: CommandDataOut | null = null;

  selectedDiagnosticsCommand?: CommandDataOut;
  selectedDiagnosticsSteps: DiagnosticsStepResult[] = [];

  private diagnosticsSubscription?: Subscription;
  private relaySubscription?: Subscription;
  private onDemandReadSubscription?: Subscription;
  // A single run can create a probe command per meter around the diagnosed one, so the reloads
  // they announce are collapsed into one.
  private readonly commandCreated = new Subject<void>();
  private commandCreatedSubscription?: Subscription;

  constructor(
    private client: Client,
    private toastr: ToastrService,
    signalRService: SignalRService,
    private diagnosticsState: DiagnosticsStateService,
    private relayState: RelayStateService,
    private onDemandReadState: OnDemandReadStateService,
  ) {
    super(signalRService);

    this.commandCreatedSubscription = this.commandCreated
      .pipe(debounceTime(500))
      .subscribe(() => this.reloadTable(this.currentPage));

    void this.diagnosticsState.ensureSubscribed();
    this.diagnosticsSubscription = this.diagnosticsState.results$.subscribe((message) => {
      // Keep an open modal live - it is opened with the steps stored so far and would
      // otherwise stay frozen until reopened. Done before the row lookup because the
      // command may not be on the page currently shown.
      if (this.selectedDiagnosticsCommand?.id === message.commandId) {
        this.selectedDiagnosticsCommand.status = message.status;
        if (message.status === CommandStatus.Failed || message.status === CommandStatus.ErrorOccured) {
          this.selectedDiagnosticsCommand.errorMessage = message.errorMessage;
        }
        this.selectedDiagnosticsSteps = message.steps ?? this.selectedDiagnosticsSteps;
      }

      const item = this.commands?.find((row) => row.id === message.commandId);
      if (!item) return;

      item.status = message.status;
      if (message.status === CommandStatus.Failed || message.status === CommandStatus.ErrorOccured) {
        item.errorMessage = message.errorMessage;
      }
    });

    // 'reads-on-demand' is owned by OnDemandReadStateService, so the row update it used to do
    // inline in registerHandlers happens here instead.
    void this.onDemandReadState.ensureSubscribed();
    this.onDemandReadSubscription = this.onDemandReadState.results$.subscribe((message) => {
      const item = this.commands?.find((row) => row.id === message.commandId);
      if (!item) return;

      item.status = message.commandStatus;
      if (message.commandStatus === CommandStatus.Failed || message.commandStatus === CommandStatus.ErrorOccured) {
        item.errorMessage = message.errorMessage;
      }
    });
  }

  override async ngOnInit(): Promise<void> {
    this.reloadTable(1);
    await super.ngOnInit();

    // 'disconnect-control' is owned by RelayStateService, so the row update it used to do
    // inline in registerHandlers happens here instead.
    void this.relayState.ensureSubscribed();
    this.relaySubscription = this.relayState.results$.subscribe((message) => {
      const item = this.commands?.find((row) => row.id === message.commandId);
      if (!item) return;

      item.status = message.commandStatus;
      if (message.commandStatus === CommandStatus.Failed || message.commandStatus === CommandStatus.ErrorOccured) {
        try {
          const parsed = JSON.parse(message.errorMessage!);
          item.errorMessage = parsed.Message;
        } catch {
          item.errorMessage = message.errorMessage;
        }
      }
    });
  }

  override async ngOnDestroy(): Promise<void> {
    this.diagnosticsSubscription?.unsubscribe();
    this.relaySubscription?.unsubscribe();
    this.onDemandReadSubscription?.unsubscribe();
    this.commandCreatedSubscription?.unsubscribe();
    await super.ngOnDestroy();
  }

  protected override registerHandlers(): void {
    const commandStatusEvents = [
      'load-profile-on-demand',
      'energy-register-on-demand',
      'power-demand-register-on-demand',
      'instantaneous-on-demand',
      'billing-data-on-demand',
      'on-demand-alarms',
      'on-demand-generic-attribute-read',
      'load-prepaid-credit-token',
      'load-clear-credit-token',
      'load-clear-tamper-token',
      'bulk-remote-token-execution',
      'load-key-change-token',
      'setup-device',
    ];

    commandStatusEvents.forEach((eventName) => {
      this.registerSignalREvent(eventName, (message) => {
        const item = this.commands?.find((row) => row.id === message.commandId);
        if (item) {
          item.status = message.commandStatus;
          if (message.commandStatus === CommandStatus.Failed || message.commandStatus === CommandStatus.ErrorOccured) {
            try {
              const parsed = JSON.parse(message.errorMessage);
              item.errorMessage = parsed.Message;
            } catch {
              item.errorMessage = message.errorMessage;
            }
          }
        }
      });
    });

    this.registerSignalREvent('image-update', (message) => {
      const item = this.commands?.find((row) => row.id === message.id);
      if (item) {
        item.status = message.status;
        if (message.status === CommandStatus.Failed || message.status === CommandStatus.ErrorOccured) {
          item.errorMessage = message.error?.message;
        }
      }
    });

    ['ping-device', 'clock-sync'].forEach((eventName) => {
      this.registerSignalREvent(eventName, (message) => {
        const item = this.commands?.find((row) => row.id === message.commandId);
        if (item) {
          item.status = message.status;
          if (message.status === CommandStatus.Failed || message.status === CommandStatus.ErrorOccured) {
            item.errorMessage = message.errorMessage;
          }
        }
      });
    });

    // A command the user just issued is not in the list this table loaded, so reload it. The
    // query filters by meter, so a command of another meter only costs the reload.
    this.registerSignalREvent('command-created', () => this.commandCreated.next());

    // Silence the warning for online-status broadcasted to the device groups
    this.registerSignalREvent('online-status', () => {});
  }

  reloadTable(page: number = 1): void {
    this.currentPage = page;
    this.requestData.pageInfo!.page = page;
    this.requestData.pageInfo!.pageSize = this.pageSize;
    this.requestData.meterId = this.meterId || undefined;
    this.client.queryCommands(this.requestData).subscribe({
      next: (response) => {
        this.commands = response.data ?? [];
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(page: number) {
    this.reloadTable(page);
  }

  onSortOption(option: string) {
    this.requestData.sorting = option === 'ASCENDING' ? SortingType.Ascending : SortingType.Descending;
    this.reloadTable(1);
  }

  onStatusOption(status: string) {
    this.requestData.status = status === 'ALL' ? undefined : (status as CommandStatus);
    this.reloadTable(1);
  }

  onTypeOption(type: string) {
    this.requestData.type = type === 'ALL' ? undefined : (type as CommandMessageType);
    this.reloadTable(1);
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.startDate = result.startDate;
    this.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.reloadTable(1);
  }

  clearDates(): void {
    this.startDate = undefined;
    this.endDate = undefined;
    this.selectedDate = '';
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable(1);
  }

  retry(event: MouseEvent, row: CommandDataOut) {
    event.stopPropagation();
    this.client.commandRetry(row.id).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error('Failed to retry command: ' + err);
      },
    });
  }

  printResponseOfCommand(event: MouseEvent, row: CommandDataOut) {
    if (row.status !== CommandStatus.Failed) return;
    event.stopPropagation();
    if (row.errorMessage) this.toastr.error(row.errorMessage);
  }

  setActiveRow(index: number | null): void {
    this.activeRow = this.activeRow === index ? null : index;
  }

  isDiagnosticsCommand(row: CommandDataOut): boolean {
    return row.type === CommandMessageType.Diagnostics;
  }

  openCommandDetails(row: CommandDataOut): void {
    if (!this.isDiagnosticsCommand(row)) return;

    this.selectedDiagnosticsCommand = row;
    this.selectedDiagnosticsSteps = [];

    // The steps live on the command, so an older run opens with what it recorded.
    this.client.getDiagnosticsStatus(row.id).subscribe({
      next: (diagnostics) => {
        if (this.selectedDiagnosticsCommand?.id !== row.id) return;
        this.selectedDiagnosticsSteps = toDiagnosticsSteps(diagnostics?.steps);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  canCancel(row: CommandDataOut): boolean {
    if ( row.type != CommandMessageType.ImageUpdate){
      return false;
    }

    return (
      row.status === CommandStatus.Pending ||
      row.status === CommandStatus.InProgress ||
      row.status === CommandStatus.Scheduled ||
      row.status === CommandStatus.Finalize
    );
  }

  openCancelModal(event: MouseEvent, row: CommandDataOut) {
    event.stopPropagation();
    this.activeRow = null;
    this.commandToCancel = row;
    this.isCancelModalOpen = true;
  }

  closeCancelModal() {
    this.isCancelModalOpen = false;
    this.commandToCancel = null;
  }

  confirmCancel() {
    const row = this.commandToCancel;
    this.isCancelModalOpen = false;
    this.commandToCancel = null;
    if (!row) return;

    this.client.cancelCommand(row.id).subscribe({
      next: (response) => {
        this.toastr.success(response);
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
